import type { MentionOptions } from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import { filter, map } from 'lodash';
import tippy, { type Instance as TippyInstance } from 'tippy.js';

import SuggestionList, { type SuggestionListRef } from './SuggestionList';

export type MentionSuggestion = {
  id: string;
  mentionLabel: string;
};

/**
 * Workaround for the current typing incompatibility between Tippy.js and Tiptap
 * Suggestion utility.
 *
 * @see https://github.com/ueberdosis/tiptap/issues/2795#issuecomment-1160623792
 *
 * Adopted from
 * https://github.com/Doist/typist/blob/a1726a6be089e3e1452def641dfcfc622ac3e942/stories/typist-editor/constants/suggestions.ts#L169-L186
 */
const DOM_RECT_FALLBACK: DOMRect = {
  bottom: 0,
  height: 0,
  left: 0,
  right: 0,
  top: 0,
  width: 0,
  x: 0,
  y: 0,
  toJSON() {
    return {};
  },
};

export interface UserOption {
  id: string;
  firstName: string;
  lastName: string;
}

export interface IssueCategoryOption {
  id: string;
  name: string;
}

type OptionType = Array<
  ({ __typename: 'User' } & UserOption) | ({ __typename: 'IssueCategory' } & IssueCategoryOption)
>;

export const mentionSuggestionOptions = (options: OptionType, freeSolo: boolean | undefined = false) => {
  const suggestions: MentionOptions['suggestion'] = {
    items: async ({ query }): Promise<MentionSuggestion[]> => {
      const items = map(options, (option) => ({
        id: option.id,
        mentionLabel: option.__typename === 'User' ? option.firstName : option.name,
      }))
        // Find matching entries based on what the user has typed so far (after
        // the @ symbol)
        .filter((item) => item.mentionLabel.toLowerCase().startsWith(query.toLowerCase()))
        .slice(0, 5);

      const hasExactMatch = items.some((item) => item.mentionLabel.toLowerCase() === query.toLowerCase());

      // gotta not pass the freesolo tag if there's an exact match present
      const result =
        freeSolo && query.trim() !== '' && !hasExactMatch
          ? filter([...items, { id: '0', mentionLabel: query }], (o) => o.mentionLabel !== '')
          : items;

      return Promise.resolve(result);
    },
    render: () => {
      let component: ReactRenderer<SuggestionListRef> | undefined;
      let popup: TippyInstance | undefined;

      return {
        onStart: (props) => {
          component = new ReactRenderer(SuggestionList, {
            props,
            editor: props.editor,
          });

          popup = tippy('body', {
            getReferenceClientRect: () => props.clientRect?.() ?? DOM_RECT_FALLBACK,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
          })[0];
        },

        onUpdate(props) {
          component?.updateProps(props);

          popup?.setProps({
            getReferenceClientRect: () => props.clientRect?.() ?? DOM_RECT_FALLBACK,
          });
        },

        onKeyDown(props) {
          if (props.event.key === 'Escape') {
            popup?.hide();
            return true;
          }

          if (!component?.ref) {
            return false;
          }

          return component.ref.onKeyDown(props);
        },

        onExit() {
          popup?.destroy();
          component?.destroy();

          // Remove references to the old popup and component upon destruction/exit.
          // (This should prevent redundant calls to `popup.destroy()`, which Tippy
          // warns in the console is a sign of a memory leak, as the `suggestion`
          // plugin seems to call `onExit` both when a suggestion menu is closed after
          // a user chooses an option, *and* when the editor itself is destroyed.)
          popup = undefined;
          component = undefined;
        },
      };
    },
  };

  return suggestions;
};
